import { Api } from '../api';
import { mapDataToInputItemsFromAppliedFilter } from '../filter/helpers';
import { ListInputItemOptions, SiftScreenEnum } from '../filter/model';
import { getAppliedFilters, updateSiftByScreen } from '../filter/slice';
import { RootState } from '../root/root-types';
import {
  GetSegmentListRequest,
  GetSegmentListResponse,
  SegmentListItem,
  CreateSegmentRequest,
  UpdateSegmentRequest,
  GetAllMetricsRequest,
  MetricsData,
  GetSegmentCustomersRequest,
  GetSegmentCustomersResponse,
  GetSegmentFilterdCustomersResponse,
  GetSegmentFilterdCustomersRequest,
  GetSegmentCustomersFiltersResponse,
  GetSegmentCustomersFiltersRequest,
  GetSegmentCustomerFiltersResponse,
  GetSegmentCustomerFiltersRequest,
  DeleteSegmentRequest,
} from './model';

export const segmentsApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    getAllSegments: builder.query<
      GetSegmentListResponse,
      GetSegmentListRequest
    >({
      query(arg) {
        return {
          url: `/hub/orgs/api/segment`,
          method: 'GET',
          params: arg,
        };
      },
    }),

    createSegment: builder.mutation<SegmentListItem, CreateSegmentRequest>({
      query(arg) {
        const { sellerWorkspaceId, body } = arg;
        return {
          url: `/hub/orgs/api/segment`,
          method: 'POST',
          params: { sellerWorkspaceId },
          body,
        };
      },
    }),

    updateSegment: builder.mutation<SegmentListItem, UpdateSegmentRequest>({
      query(arg) {
        const { segmentId, sellerWorkspaceId, body } = arg;
        return {
          url: `/hub/orgs/api/segment/${segmentId}`,
          method: 'PUT',
          params: { sellerWorkspaceId },
          body,
        };
      },
    }),

    deleteSegment: builder.mutation<any, DeleteSegmentRequest>({
      query(arg) {
        const { segmentId, sellerWorkspaceId } = arg;
        return {
          url: `/hub/orgs/api/segment/${segmentId}`,
          method: 'DELETE',
          params: { sellerWorkspaceId },
        };
      },
    }),

    getAllMetrics: builder.query<MetricsData[], GetAllMetricsRequest>({
      query(arg) {
        return {
          url: `/hub/orgs/api/metrics`,
          method: 'GET',
          params: arg,
        };
      },
    }),

    getSegmentCustomers: builder.mutation<
      GetSegmentCustomersResponse,
      GetSegmentCustomersRequest
    >({
      query(arg) {
        return {
          url: `/hub/reports/api/v2/segment-membership/details`,
          method: 'POST',
          body: arg,
        };
      },
    }),

    getSegmentCustomerFilters: builder.mutation<
      GetSegmentCustomerFiltersResponse,
      GetSegmentCustomerFiltersRequest
    >({
      query(arg) {
        return {
          url: `/hub/reports/api/v2/segment-membership/details/filter`,
          method: 'POST',
          body: arg,
        };
      },
      onQueryStarted(request, { dispatch, queryFulfilled, getState }) {
        if (request?.filterScreen as SiftScreenEnum) {
          queryFulfilled?.then((response) => {
            const appliedFilters = getAppliedFilters(getState() as RootState);
            const routes = mapDataToInputItemsFromAppliedFilter(
              appliedFilters,
              response?.data?.routes,
              'name',
              'name',
              ListInputItemOptions.segmentRoutes
            )?.filter((x: any) => x.value);

            const segments = mapDataToInputItemsFromAppliedFilter(
              appliedFilters,
              response?.data?.segments,
              'name',
              'name',
              ListInputItemOptions.segments
            )?.filter((x: any) => x.value);

            dispatch(
              updateSiftByScreen({
                screen: request?.filterScreen,
                filters: {
                  routes: routes ?? [],
                  segments: segments ?? [],
                },
              })
            );
          });
        }
      },
    }),

    getSegmentFilterdCustomers: builder.mutation<
      GetSegmentFilterdCustomersResponse,
      GetSegmentFilterdCustomersRequest
    >({
      query(arg) {
        return {
          url: `/hub/reports/api/v2/segment-membership/customers`,
          method: 'POST',
          body: arg,
        };
      },
    }),

    getSegmentCustomersFilters: builder.mutation<
      GetSegmentCustomersFiltersResponse,
      GetSegmentCustomersFiltersRequest
    >({
      query(arg) {
        return {
          url: `/hub/reports/api/v2/segment-membership/customers/filter`,
          method: 'POST',
          body: arg,
        };
      },

      onQueryStarted(request, { dispatch, queryFulfilled, getState }) {
        if (request?.filterScreen as SiftScreenEnum) {
          queryFulfilled
            ?.then((response) => {
              const appliedFilters = getAppliedFilters(getState() as RootState);
              const routes = mapDataToInputItemsFromAppliedFilter(
                appliedFilters,
                response?.data?.routes,
                'name',
                'name',
                ListInputItemOptions.segmentRoutes
              )?.filter((x: any) => x.value);

              const segments = mapDataToInputItemsFromAppliedFilter(
                appliedFilters,
                response?.data?.segments,
                'name',
                'name',
                ListInputItemOptions.segments
              )?.filter((x: any) => x.value);

              dispatch(
                updateSiftByScreen({
                  screen: request?.filterScreen,
                  filters: {
                    routes: routes ?? [],
                    segments: segments ?? [],
                  },
                })
              );
            })
            ?.catch(() => {
              // Request failed (e.g. 422/500); handled by caller via unwrap().catch()
            });
        }
      },
    }),
    // /orgs/api/segment/
  }),
});

export const {
  useLazyGetAllSegmentsQuery,
  useCreateSegmentMutation,
  useLazyGetAllMetricsQuery,
  useUpdateSegmentMutation,
  useGetSegmentCustomersMutation,
  useGetSegmentFilterdCustomersMutation,
  useGetSegmentCustomersFiltersMutation,
  useGetSegmentCustomerFiltersMutation,
  useDeleteSegmentMutation,
} = segmentsApi;
